import { useCallback, useMemo } from 'react';

import { type Vendor } from '@sg/graphql-schema';
import { type useCustomizationExternalActions } from '@sg/shared-fe-features-customization';

import { useAppsFlyer } from '@order/integrations-appsflyer';
import { getSharedLineItemProductName } from '@order/shared-line-item';
import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';
import { convertPriceToFloat } from '@order/utils';

import {
  useAddLineItemToCart,
  useCart,
  useEditLineItemInCart,
  useRemoveLineItemFromCart,
} from '../../../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const useCustomizeCartMutations = (params: Params) => {
  const {
    addressId,
    deliveryFee,
    vendor,
    onAddLineItemToCart,
    onEditLineItemInCart,
    onRemoveLineItemFromCart,
    sharedLineItemSlug,
  } = params;

  const { cart, cartOrderType, total, orderId, isGroupOrder } = useCart();
  const { track } = useTelemetry();
  const { logAddToCartEvent } = useAppsFlyer();

  const addLineItemToCartMutation = useAddLineItemToCart({ onSuccess: onAddLineItemToCart }); // prettier-ignore
  const editLineItemInCartMutation = useEditLineItemInCart({ onSuccess: onEditLineItemInCart }); // prettier-ignore
  const removeFromCartMutation = useRemoveLineItemFromCart({ onSuccess: onRemoveLineItemFromCart }); // prettier-ignore

  const { addLineItemToCart, fetching: isAddingLineItemToCart } = addLineItemToCartMutation; // prettier-ignore
  const { editLineItemInCart, fetching: isEditingLineItemInCart } = editLineItemInCartMutation; // prettier-ignore
  const { removeLineItemFromCart, fetching: isRemovingLineItemFromCart } = removeFromCartMutation; // prettier-ignore

  // ─── Derived Data ────────────────────────────────────────────────────────────

  const deliveryDetails = useMemo(() => {
    if (!addressId) return;

    return {
      tip: 0,
      addressId,
      vendor: vendor?.name ?? '',
      vendorRestaurantId: vendor?.restaurantId ?? '',
      deliveryFee: deliveryFee ?? 0,
    };
  }, [vendor, addressId, deliveryFee]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const onAddLineItemToCartTelemetry = useCallback(
    (params: OnCustomizationCompleteParams) => {
      const { product, quantity, restaurant, modifications, lineItemId, totalCost } = params;

      const resolvedSharedLineItemProductName = sharedLineItemSlug
        ? getSharedLineItemProductName({ sharedLineItemSlug })
        : undefined;

      track('pdp.add_to_bag', {
        orderId,
        restaurantId: restaurant?.id,
        restaurantName: restaurant?.name,
        orderValue: total,
        orderType: cartOrderType,
        bagCount: cart?.lineItems?.length,
        isGroupOrder,
        ...(sharedLineItemSlug
          ? {
              isSharedLineItem: true,
              sharedLineItemProductName: resolvedSharedLineItemProductName,
              sharedLineItemSlug,
            }
          : {}),
        selectedProduct: telemetryProductFromInput(
          {
            ...product,
            quantity,
            location: restaurant?.slug,
            sharedLineItemProductName: resolvedSharedLineItemProductName,
            sharedLineItemSlug,
          },
          modifications,
        ),
      });

      if (lineItemId) return;

      logAddToCartEvent({
        price: convertPriceToFloat(totalCost),
        contentId: product?.id ?? '',
        contentType: product?.name ?? 'product',
        quantity: quantity ?? 1,
      });
    },
    [
      cart?.lineItems?.length,
      cartOrderType,
      isGroupOrder,
      logAddToCartEvent,
      orderId,
      sharedLineItemSlug,
      total,
      track,
    ],
  );

  const modifyCart = useCallback(
    async (params: OnCustomizationCompleteParams) => {
      const { product, lineItemId, quantity, modifications, mixedDressingDetails, totalCost } =
        params;

      if (!product) return;

      onAddLineItemToCartTelemetry(params);

      const modificationsInput = {
        additions: modifications?.additions ?? [],
        removals: modifications?.removals ?? [],
        substitutions: modifications?.substitutions ?? [],
        mixedDressingDetails: mixedDressingDetails ?? [],
      };

      // ─── Edit Line Item ──────────────────────────────────────────

      if (lineItemId) {
        await editLineItemInCart({
          product,
          lineItemId,
          quantity,
          modifications: modificationsInput,
        });

        return;
      }

      // ─── Add Line Item ────────────────────────────────────────────

      await addLineItemToCart({
        product,
        quantity,
        deliveryDetails,
        sharedLineItemSlug,
        totalCost,
        modifications: modificationsInput,
      });
    },
    [
      onAddLineItemToCartTelemetry,
      addLineItemToCart,
      deliveryDetails,
      editLineItemInCart,
      sharedLineItemSlug,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    removeLineItemFromCart,
    modifyCart,
    isAddingLineItemToCart,
    isEditingLineItemInCart,
    isRemovingLineItemFromCart,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  addressId?: string;
  deliveryFee?: number;
  vendor?: Vendor;
  onAddLineItemToCart?: () => void;
  onEditLineItemInCart?: () => void;
  onRemoveLineItemFromCart?: () => void;
  sharedLineItemSlug?: string;
};

type OnCustomizationCompleteParams = NonNullable<
  Parameters<Callbacks['onCustomizationComplete']>[0]
>;

type Callbacks = Required<Parameters<typeof useCustomizationExternalActions>[0]>;
